import React, { useState } from 'react';
import './ShopRatingForm.css';
import { useAuth } from '../context/AuthContext';

function ShopRatingForm({ shopId, onSubmitted }) {
  const { user, fetchWithAuth } = useAuth();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setError('Du musst angemeldet sein, um eine Bewertung zu schreiben.');
      return;
    }

    if (!comment.trim()) {
      setError('Bitte schreib einen Kommentar zu deiner Erfahrung.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await fetchWithAuth('/api/ratings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          targetType: 'store',
          targetId: shopId,
          stars: rating,
          comment: comment.trim(),
          isAnonymous: isAnonymous
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Fehler beim Speichern der Bewertung');
      }

      setSuccess('✅ Deine Bewertung wurde gespeichert! Danke für dein Feedback!');
      setComment('');
      setRating(5);
      setIsAnonymous(false);

      // Nach 2 Sekunden die Seite aktualisieren
      setTimeout(() => {
        if (onSubmitted) {
          onSubmitted();
        }
      }, 1500);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Fehler beim Speichern der Bewertung');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="shop-rating-form login-prompt">
        <p>🔐 Bitte <strong>melde dich an</strong>, um eine Bewertung zu schreiben.</p>
      </div>
    );
  }

  return (
    <div className="shop-rating-form-container">
      <h3>Schreib deine Erfahrung</h3>
      <p className="form-subtitle">
        Hilf anderen Growern, die richtige Entscheidung zu treffen!
      </p>

      <form onSubmit={handleSubmit} className="shop-rating-form">
        {/* Rating Sterne */}
        <div className="form-group">
          <label>Deine Bewertung:</label>
          <div className="star-rating">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                type="button"
                className={`star-btn ${star <= rating ? 'active' : ''}`}
                onClick={() => setRating(star)}
              >
                ⭐
              </button>
            ))}
            <span className="rating-text">{rating} von 5 Sternen</span>
          </div>
        </div>

        {/* Anonymität Toggle */}
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="checkbox-input"
            />
            <span className="checkbox-text">
              {isAnonymous ? '🕵️ Anonym bewerten' : '👤 Mit meinem Namen bewerten'}
            </span>
          </label>
          <p className="checkbox-hint">
            {isAnonymous 
              ? 'Deine Bewertung wird ohne deinen Namen veröffentlicht'
              : 'Dein Name und Profilbild werden zusammen mit deiner Bewertung angezeigt'}
          </p>
        </div>

        {/* Comment */}
        <div className="form-group">
          <label htmlFor="comment">Dein Erfahrungsbericht:</label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Teile deine Erfahrung mit diesem Shop...
              
Zum Beispiel:
- Wie war die Bestellabwicklung?
- War die Lieferung schnell?
- Wie ist die Qualität der Produkte?
- Würdest du wieder hier bestellen?"
            maxLength={2000}
            rows={6}
            className="comment-textarea"
          />
          <div className="char-count">
            {comment.length} / 2000 Zeichen
          </div>
        </div>

        {/* Hinweis */}
        <div className="form-hint">
          <strong>💡 Tipp:</strong> Je detaillierter deine Bewertung, desto hilfreicher ist sie für andere!
        </div>

        {/* Errors und Success */}
        {error && <div className="form-error">{error}</div>}
        {success && <div className="form-success">{success}</div>}

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="submit"
            disabled={loading || !comment.trim()}
            className="btn-submit"
          >
            {loading ? '⏳ Wird gespeichert...' : '✨ Bewertung absenden'}
          </button>
        </div>

        <p className="form-note">
          📋 Deine Bewertung wird sofort veröffentlicht. Bitte halte dich an unsere
          <a href="/forum" target="_blank" rel="noopener noreferrer"> Community Richtlinien</a>.
        </p>
      </form>
    </div>
  );
}

export default ShopRatingForm;
